// Sponsor Ads CRUD — property-scoped. Each ad becomes a picker option
// in the "Publish to Facebook" panel and gets baked into the featured
// image at publish time. WordPress is untouched.
import { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Megaphone, Plus, Loader2, Save, X, Trash2, Upload, ImageOff,
  Pencil, BarChart3, ChevronRight, ChevronDown, ExternalLink,
  RefreshCw, Heart, MessageSquare, Share2, LayoutDashboard,
  Sparkles, Eye, Stethoscope, Link2, Copy, Ban, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdStyleEditor from "@/pages/AdStyleEditor";
import PasteImageButton from "@/components/PasteImageButton";

const EMPTY_AD = {
  name: "",
  title: "",
  subhead: "",
  call_to_action: "",
  message_for_post: "",
  phone: "",
  website: "",
  email: "",
  background_color: "#111827",
  logo_path: "",
  active: true,
};

function logoUrlFor(path) {
  if (!path) return "";
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
}

export default function SponsorAdsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);   // ad doc being edited, or {new: true, ...EMPTY_AD}
  const [stylingAd, setStylingAd] = useState(null); // ad whose style editor is open

  const { data: ads = [], isLoading } = useQuery({
    queryKey: ["ads"],
    queryFn: () => api.listAds(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteAd(id),
    onSuccess: () => {
      toast.success("Ad deleted");
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline mb-1">Newsroom · Facebook overlays</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight">
              Sponsor ads
            </h1>
            <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-2xl">
              Ads you save here can be dropped onto any Facebook post&apos;s
              featured image at publish time. WordPress images are never
              modified — the overlay only lives on the FB copy.
            </p>
          </div>
          <button
            data-testid="ads-new-btn"
            onClick={() => setEditing({ new: true, ...EMPTY_AD })}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[var(--slate-900)] text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> New ad
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-10 py-6 sm:py-8">
        <div className="max-w-3xl mb-6 space-y-3">
          <StatsRefreshStatus />
          <SnippetsAccordion />
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : ads.length === 0 ? (
          <div
            data-testid="ads-empty-state"
            className="max-w-md text-center py-16 mx-auto text-[var(--ink-muted)]"
          >
            <Megaphone className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">
              No sponsor ads yet. Add one to promote clients on your
              Facebook posts.
            </p>
          </div>
        ) : (
          <ul className="max-w-3xl space-y-3">
            {ads.map((ad) => (
              <AdRow
                key={ad.id}
                ad={ad}
                onEdit={() => setEditing(ad)}
                onStyleEdit={() => setStylingAd(ad)}
                onDelete={() => {
                  if (window.confirm(`Delete "${ad.name}"?`)) {
                    deleteMutation.mutate(ad.id);
                  }
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <AdEditor
          initial={editing}
          isNew={!!editing.new}
          onClose={() => setEditing(null)}
        />
      )}
      {stylingAd && (
        <AdStyleEditor
          ad={stylingAd}
          onClose={() => setStylingAd(null)}
        />
      )}
    </div>
  );
}

function SnippetsAccordion() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftText, setDraftText] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["sponsor-snippets"],
    queryFn: () => api.listSponsorSnippets(),
    enabled: open,
  });
  const snippets = data?.snippets || [];
  const variables = data?.variables || [];

  const create = useMutation({
    mutationFn: (text) => api.createSponsorSnippet({ text, active: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sponsor-snippets"] });
      setDrafting(false);
      setDraftText("");
      toast.success("Snippet added");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Add failed"),
  });

  const patch = useMutation({
    mutationFn: ({ id, patch }) => api.patchSponsorSnippet(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sponsor-snippets"] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.deleteSponsorSnippet(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sponsor-snippets"] });
      toast.success("Snippet deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  return (
    <div
      data-testid="snippets-accordion"
      className="bg-white border border-[var(--border)]"
    >
      <button
        data-testid="snippets-accordion-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--paper-hover,#fafaf8)]"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-[var(--ink-muted)]" />
        ) : (
          <ChevronRight className="w-4 h-4 text-[var(--ink-muted)]" />
        )}
        <Sparkles className="w-4 h-4 text-[var(--ink-muted)]" />
        <div className="flex-1">
          <div className="text-sm font-medium">FB caption snippets</div>
          <div className="text-xs text-[var(--ink-muted)] mt-0.5">
            One random snippet is appended to every FB post that includes a sponsor ad.
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-[var(--border)] px-4 py-4 space-y-4">
          <div className="text-[11px] text-[var(--ink-muted)] leading-snug">
            Available variables (paste into any snippet):{" "}
            <span className="font-mono-display text-[10px]">
              {variables.join("  ·  ")}
            </span>
          </div>

          {isLoading ? (
            <div className="text-xs text-[var(--ink-muted)] flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading snippets…
            </div>
          ) : (
            <ul className="space-y-2" data-testid="snippets-list">
              {snippets.map((s) => (
                <SnippetRow
                  key={s.id}
                  snippet={s}
                  onToggle={() =>
                    patch.mutate({ id: s.id, patch: { active: !s.active } })
                  }
                  onSave={(text) => patch.mutate({ id: s.id, patch: { text } })}
                  onDelete={() => {
                    if (window.confirm("Delete this snippet?")) del.mutate(s.id);
                  }}
                />
              ))}
              {snippets.length === 0 && (
                <li className="text-xs text-[var(--ink-muted)] italic">
                  No snippets. Add one to enable sponsor caption tails.
                </li>
              )}
            </ul>
          )}

          {drafting ? (
            <div className="space-y-2">
              <textarea
                data-testid="snippet-new-textarea"
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                rows={5}
                placeholder={"Presented by SPONSOR_NAME\n\nSPONSOR_CALL_TO_ACTION\n\nLearn more:\nSPONSOR_WEBSITE"}
                className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] font-mono-display resize-y"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button
                  data-testid="snippet-new-save"
                  onClick={() => {
                    if (!draftText.trim()) {
                      toast.error("Snippet text is required");
                      return;
                    }
                    create.mutate(draftText.trim());
                  }}
                  disabled={create.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50"
                >
                  {create.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  Add snippet
                </button>
                <button
                  onClick={() => { setDrafting(false); setDraftText(""); }}
                  className="px-3 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--paper-hover,#fafaf8)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              data-testid="snippet-add-btn"
              onClick={() => setDrafting(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--paper-hover,#fafaf8)]"
            >
              <Plus className="w-3.5 h-3.5" /> Add snippet
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function SnippetRow({ snippet, onToggle, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(snippet.text || "");
  return (
    <li
      data-testid={`snippet-row-${snippet.id}`}
      className={`border border-[var(--border)] p-3 ${snippet.active ? "" : "opacity-60"}`}
    >
      {editing ? (
        <div className="space-y-2">
          <textarea
            data-testid={`snippet-textarea-${snippet.id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] font-mono-display resize-y"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <button
              data-testid={`snippet-save-${snippet.id}`}
              onClick={() => {
                onSave(text.trim());
                setEditing(false);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:opacity-90"
            >
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button
              onClick={() => { setText(snippet.text || ""); setEditing(false); }}
              className="px-3 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--paper-hover,#fafaf8)]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <pre className="flex-1 text-xs font-mono-display whitespace-pre-wrap leading-relaxed text-[var(--ink)] m-0">
            {snippet.text}
          </pre>
          <div className="flex items-center gap-1 shrink-0">
            <label
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--ink-muted)] cursor-pointer select-none"
              data-testid={`snippet-active-${snippet.id}`}
            >
              <input
                type="checkbox"
                checked={!!snippet.active}
                onChange={onToggle}
                className="accent-[var(--slate-900)]"
              />
              {snippet.active ? "Active" : "Off"}
            </label>
            <button
              data-testid={`snippet-edit-${snippet.id}`}
              onClick={() => setEditing(true)}
              className="p-1.5 hover:bg-[var(--paper-hover,#fafaf8)]"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid={`snippet-delete-${snippet.id}`}
              onClick={onDelete}
              className="p-1.5 hover:bg-[var(--paper-hover,#fafaf8)] text-red-600"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </li>
  );
}


function AdEditor({ initial, isNew, onClose }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(() => ({ ...initial }));
  const logoInputRef = useRef(null);

  const uploadLogo = useMutation({
    mutationFn: (file) => api.uploadAdLogo(file),
    onSuccess: (r) => {
      setDraft((d) => ({ ...d, logo_path: r.logo_path }));
      toast.success("Logo uploaded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = { ...draft };
      delete payload.new;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.property_id;
      return isNew
        ? api.createAd(payload)
        : api.patchAd(initial.id, payload);
    },
    onSuccess: () => {
      toast.success(isNew ? "Ad created" : "Ad saved");
      qc.invalidateQueries({ queryKey: ["ads"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="ad-editor-modal"
        className="mt-10 mb-10 w-full max-w-lg bg-white shadow-lg border border-[var(--border)]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h2 className="font-serif-display text-xl">
            {isNew ? "New sponsor ad" : `Edit “${initial.name}”`}
          </h2>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--ink)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Internal name" required>
            <input
              data-testid="ad-field-name"
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="e.g. Baja Water Systems"
              className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title">
              <input
                data-testid="ad-field-title"
                value={draft.title}
                onChange={(e) => set({ title: e.target.value })}
                placeholder="Pure Water."
                className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              />
            </Field>
            <Field label="Subhead">
              <input
                data-testid="ad-field-subhead"
                value={draft.subhead}
                onChange={(e) => set({ subhead: e.target.value })}
                placeholder="Better Life."
                className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              />
            </Field>
          </div>
          <Field
            label="Call to action"
            hint="Used in FB caption snippets as SPONSOR_CALL_TO_ACTION. Not printed on the image."
          >
            <textarea
              data-testid="ad-field-cta"
              value={draft.call_to_action}
              onChange={(e) => set({ call_to_action: e.target.value })}
              placeholder="Book a free water test this month."
              rows={2}
              className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] resize-y"
            />
          </Field>
          <Field
            label="Message for post:"
            hint="A line or two for the Facebook post itself — an offer, an event, a seasonal note. Used in FB caption snippets as SPONSOR_MESSAGE. Not printed on the image."
          >
            <textarea
              data-testid="ad-field-message-for-post"
              value={draft.message_for_post || ""}
              onChange={(e) => set({ message_for_post: e.target.value })}
              placeholder="Mention this post for 10% off your first delivery."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] resize-y"
            />
          </Field>
          <Field label="Phone">
            <input
              data-testid="ad-field-phone"
              value={draft.phone}
              onChange={(e) => set({ phone: e.target.value })}
              placeholder="(612) 123 4567"
              className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            />
          </Field>
          <Field label="Website">
            <input
              data-testid="ad-field-website"
              value={draft.website}
              onChange={(e) => set({ website: e.target.value })}
              placeholder="bajawatersystems.com"
              className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            />
          </Field>
          <Field label="Email">
            <input
              data-testid="ad-field-email"
              value={draft.email}
              onChange={(e) => set({ email: e.target.value })}
              placeholder="hello@bajawatersystems.com"
              className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            />
          </Field>
          <Field label="Background color">
            <div className="flex items-center gap-2">
              <input
                data-testid="ad-field-color-picker"
                type="color"
                value={draft.background_color}
                onChange={(e) => set({ background_color: e.target.value })}
                className="h-9 w-14 border border-[var(--border)] cursor-pointer bg-transparent"
              />
              <input
                data-testid="ad-field-color-hex"
                value={draft.background_color}
                onChange={(e) => set({ background_color: e.target.value })}
                className="flex-1 px-3 py-2 text-sm font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              />
            </div>
          </Field>
          <Field label="Logo (transparent PNG)">
            <div className="flex items-center gap-3">
              <div
                className="w-16 h-16 shrink-0 grid place-items-center border border-[var(--border)]"
                style={{ backgroundColor: draft.background_color || "#111827" }}
              >
                {draft.logo_path ? (
                  <img
                    src={logoUrlFor(draft.logo_path)}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ImageOff className="w-4 h-4 text-white/60" />
                )}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadLogo.mutate(f);
                }}
              />
              <button
                type="button"
                data-testid="ad-field-logo-upload"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadLogo.isPending}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50"
              >
                {uploadLogo.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {draft.logo_path ? "Replace logo" : "Upload logo"}
              </button>
              {/* The ads endpoints key the stored extension off the
                  content type and expect PNG/JPEG/WebP, so `accept`
                  is narrowed here — anything else on the clipboard
                  (Safari's image/tiff, say) is re-encoded to PNG
                  before it is sent. */}
              <PasteImageButton
                onImage={(file) => uploadLogo.mutate(file)}
                accept="image/png,image/jpeg,image/webp"
                busy={uploadLogo.isPending}
                testId="ad-field-logo-paste"
                label="Paste"
                title="Paste a logo from the clipboard (or press Cmd/Ctrl+V while this editor is open)"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50"
              />
              {draft.logo_path && (
                <button
                  type="button"
                  data-testid="ad-field-logo-remove"
                  onClick={() => set({ logo_path: "" })}
                  className="text-xs text-[var(--ink-muted)] hover:text-red-600"
                >
                  Remove
                </button>
              )}
            </div>
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--border)] bg-[var(--secondary)]">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"
          >
            Cancel
          </button>
          <button
            data-testid="ad-editor-save-btn"
            onClick={() => save.mutate()}
            disabled={!draft.name.trim() || save.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-40"
          >
            {save.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider text-[var(--ink-muted)] mb-1.5 font-mono-display">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {children}
      {hint && (
        <div className="text-[11px] text-[var(--ink-muted)] mt-1.5 leading-snug">
          {hint}
        </div>
      )}
    </label>
  );
}

function AdRow({ ad, onEdit, onStyleEdit, onDelete }) {
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  return (
    <li
      data-testid={`ad-row-${ad.id}`}
      className="bg-white border border-[var(--border)]"
    >
      <div className="flex items-center gap-4 p-4">
        <div
          className="w-14 h-14 shrink-0 border border-[var(--border)] grid place-items-center overflow-hidden"
          style={{ backgroundColor: ad.background_color || "#111827" }}
        >
          {ad.logo_path ? (
            <img
              src={logoUrlFor(ad.logo_path)}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <ImageOff className="w-4 h-4 text-white/60" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{ad.name}</div>
          <div className="text-xs text-[var(--ink-muted)] truncate">
            {[ad.title, ad.subhead].filter(Boolean).join(" · ") ||
              <em>No title/subhead</em>}
          </div>
          <div className="text-[11px] text-[var(--ink-muted)] font-mono-display truncate mt-0.5">
            {[ad.phone, ad.website, ad.email].filter(Boolean).join("  ·  ") ||
              "no contact info"}
          </div>
        </div>
        <button
          data-testid={`ad-style-editor-open-${ad.id}`}
          onClick={onStyleEdit}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)]"
        >
          <LayoutDashboard className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Edit styles</span>
        </button>
        <button
          data-testid={`ad-analytics-toggle-${ad.id}`}
          onClick={() => setAnalyticsOpen((v) => !v)}
          className={
            "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border " +
            (analyticsOpen
              ? "border-[var(--ink)] bg-[var(--ink)] text-white"
              : "border-[var(--border)] hover:border-[var(--ink)]")
          }
          aria-label="Analytics"
        >
          {analyticsOpen ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
          <BarChart3 className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Analytics</span>
        </button>
        <button
          data-testid={`ad-edit-${ad.id}`}
          onClick={onEdit}
          className="p-2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
          aria-label="Edit ad"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          data-testid={`ad-delete-${ad.id}`}
          onClick={onDelete}
          className="p-2 text-[var(--ink-muted)] hover:text-red-600"
          aria-label="Delete ad"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {analyticsOpen && <AdAnalyticsPanel adId={ad.id} />}
    </li>
  );
}

function AdAnalyticsPanel({ adId }) {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  // Two-tier state: `pendingSince/Until` = what's typed into the
  // inputs, `since/until` = what's actually applied to the query.
  // Filter fires ONLY when the editor clicks Apply (or clears). This
  // prevents the "type one date and it auto-fires with the other end
  // still empty" UX that showed everything from now to forever until
  // the second date landed.
  const [pendingSince, setPendingSince] = useState("");
  const [pendingUntil, setPendingUntil] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const range = since || until ? { since, until } : null;
  const dirty = pendingSince !== since || pendingUntil !== until;
  // Diagnostic modal state — populated by the per-row 🩺 button.
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["ad-analytics", adId, since || null, until || null],
    queryFn: () => api.getAdAnalytics(adId, false, range),
  });

  const applyRange = () => {
    setSince(pendingSince);
    setUntil(pendingUntil);
  };
  const clearRange = () => {
    setPendingSince("");
    setPendingUntil("");
    setSince("");
    setUntil("");
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.getAdAnalytics(adId, true, range);
      await refetch();
      const scope = range ? " for the filtered date range" : "";
      toast.success(`Metrics refreshed from Facebook${scope}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const openDiagnose = async (placementId) => {
    setDiag({ loading: true });
    setDiagLoading(true);
    try {
      const d = await api.diagnosePlacement(placementId);
      setDiag(d);
    } catch (e) {
      setDiag({ error: e?.response?.data?.detail || e.message });
    } finally {
      setDiagLoading(false);
    }
  };
  const closeDiagnose = () => setDiag(null);

  // Editor-driven removal of a single placement row. We only touch the
  // local registry — the FB post itself (if it still exists) is
  // untouched, and would come back on a full refresh. In practice the
  // editor only clicks this after they've already deleted the post on
  // Facebook, so refresh returns a 404 that stays out of our list.
  const deletePlacement = useMutation({
    mutationFn: (placementId) => api.deleteAdPlacement(adId, placementId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ad-analytics", adId] });
      toast.success("Placement removed from analytics");
    },
    onError: (e) => {
      toast.error(
        e?.response?.data?.detail || e.message || "Delete failed",
      );
    },
  });

  const handleDeletePlacement = (p) => {
    const preview = (p.article_headline || "(no headline)").slice(0, 80);
    if (
      !window.confirm(
        `Remove this placement from analytics?\n\n"${preview}"\n\n` +
          "This only removes the registry entry — the Facebook post itself " +
          "is not affected. If the post still exists on Facebook, it will " +
          "reappear the next time you Refresh from Facebook.",
      )
    )
      return;
    deletePlacement.mutate(p.id);
  };

  if (isLoading) {
    return (
      <div className="border-t border-[var(--border)] p-4 flex items-center gap-2 text-xs text-[var(--ink-muted)]">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading analytics…
      </div>
    );
  }
  if (error) {
    return (
      <div className="border-t border-[var(--border)] p-4 text-xs text-red-600">
        Failed to load analytics: {error.message}
      </div>
    );
  }
  const totals =
    data?.totals || {
      reactions: 0, comments: 0, shares: 0, impressions: 0,
      engagement: 0, posts: 0,
    };
  const placements = data?.placements || [];

  return (
    <div
      data-testid={`ad-analytics-${adId}`}
      className="border-t border-[var(--border)] bg-[var(--secondary)]"
    >
      {/* Date-range filter — placed above the roll-up cards so the
          editor understands the totals reflect the current filter.
          Filter fires ONLY on Apply so partially-typed dates don't
          trigger a weird half-range fetch. */}
      <div className="px-4 pt-4 flex flex-wrap items-center gap-2 text-[11px] font-mono-display">
        <span className="uppercase tracking-wider text-[var(--ink-muted)]">
          Date range
        </span>
        <input
          type="date"
          data-testid={`ad-analytics-since-${adId}`}
          value={pendingSince}
          onChange={(e) => setPendingSince(e.target.value)}
          max={pendingUntil || undefined}
          className="border border-[var(--border)] bg-white px-2 py-1 text-xs"
          aria-label="From date"
        />
        <span className="text-[var(--ink-muted)]">→</span>
        <input
          type="date"
          data-testid={`ad-analytics-until-${adId}`}
          value={pendingUntil}
          onChange={(e) => setPendingUntil(e.target.value)}
          min={pendingSince || undefined}
          className="border border-[var(--border)] bg-white px-2 py-1 text-xs"
          aria-label="To date"
        />
        <button
          type="button"
          data-testid={`ad-analytics-apply-range-${adId}`}
          onClick={applyRange}
          disabled={!dirty}
          className="px-3 py-1 text-[10px] uppercase tracking-wider bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Apply
        </button>
        {(since || until || pendingSince || pendingUntil) && (
          <button
            type="button"
            data-testid={`ad-analytics-clear-range-${adId}`}
            onClick={clearRange}
            className="text-[10px] uppercase tracking-wider text-[var(--accent)] hover:underline"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-[10px] text-[var(--ink-muted)] normal-case tracking-normal">
          Per-post stats are lifetime — the range only filters WHICH posts count.
        </span>
      </div>
      {/* Roll-up cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-4">
        <StatCard label="Posts" value={totals.posts} icon={BarChart3} testId="ad-stat-posts" />
        <StatCard label="Reactions" value={totals.reactions} icon={Heart} testId="ad-stat-reactions" />
        <StatCard label="Comments" value={totals.comments} icon={MessageSquare} testId="ad-stat-comments" />
        <StatCard label="Shares" value={totals.shares} icon={Share2} testId="ad-stat-shares" />
        <StatCard label="Views" value={totals.impressions || 0} icon={Eye} testId="ad-stat-impressions" />
      </div>
      {/* Per-post breakdown */}
      {placements.length === 0 ? (
        <div className="text-xs text-[var(--ink-muted)] px-4 pb-4">
          {(since || until)
            ? "No Facebook posts using this ad match the current date range."
            : "No Facebook posts yet used this ad. Publish an article with this ad selected and the metrics will start appearing here."}
        </div>
      ) : (
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">
              {(since || until) ? "Filtered posts" : "Every post that used this ad"}
            </div>
            <button
              data-testid={`ad-refresh-metrics-${adId}`}
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 text-[11px] text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-50"
              title={
                (since || until)
                  ? "Re-fetches Meta only for the placements in the current date range"
                  : "Re-fetches Meta for every placement of this ad"
              }
            >
              {refreshing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Refresh from Facebook
            </button>
          </div>
          <ul className="space-y-1.5">
            {placements.map((p) => (
              <li
                key={p.id}
                data-testid={`ad-placement-${p.id}`}
                className="flex items-center gap-3 bg-white border border-[var(--border)] px-3 py-2 text-xs"
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">
                    {p.article_headline || "(no headline)"}
                  </div>
                  <div className="text-[10px] text-[var(--ink-muted)] font-mono-display truncate">
                    {p.style} · {p.background_mode} ·{" "}
                    {p.page_name || p.page_id} ·{" "}
                    {new Date(p.published_at).toLocaleDateString()}
                    {p.metrics_stale && (
                      <span
                        className="ml-2 text-amber-600"
                        title={p.metrics_stale_reason || undefined}
                      >
                        (stale{p.metrics_stale_reason ? ` — ${p.metrics_stale_reason}` : " — post deleted or page disconnected"})
                      </span>
                    )}
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-3 font-mono-display text-[11px]">
                  <span className="inline-flex items-center gap-1" title="Reactions">
                    <Heart className="w-3 h-3" />
                    {(p.metrics?.reactions) || 0}
                  </span>
                  <span className="inline-flex items-center gap-1" title="Comments">
                    <MessageSquare className="w-3 h-3" />
                    {(p.metrics?.comments) || 0}
                  </span>
                  <span className="inline-flex items-center gap-1" title="Shares">
                    <Share2 className="w-3 h-3" />
                    {(p.metrics?.shares) || 0}
                  </span>
                  <span
                    className="inline-flex items-center gap-1"
                    title={
                      p.metrics?.impressions == null
                        ? "Views unavailable — reconnect the page in Settings → Social"
                        : "Impressions (views on Facebook)"
                    }
                  >
                    <Eye className="w-3 h-3" />
                    {/* `impressions` is intentionally nullable now:
                        null = Meta couldn't return insights data (
                        missing `read_insights`, page below 100
                        followers, or fresh post pre-daily-rollup).
                        A real 0 still renders as 0. */}
                    {p.metrics?.impressions == null
                      ? "—"
                      : p.metrics.impressions}
                  </span>
                </div>
                {p.permalink_url && (
                  <a
                    href={p.permalink_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--ink-muted)] hover:text-[var(--accent)]"
                    aria-label="Open post on Facebook"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                <button
                  type="button"
                  data-testid={`ad-placement-diagnose-${p.id}`}
                  onClick={() => openDiagnose(p.id)}
                  aria-label="Diagnose why these metrics look wrong"
                  title="Diagnose why these metrics look wrong"
                  className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
                >
                  <Stethoscope className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  data-testid={`ad-placement-delete-${p.id}`}
                  onClick={() => handleDeletePlacement(p)}
                  disabled={
                    deletePlacement.isPending &&
                    deletePlacement.variables === p.id
                  }
                  aria-label="Remove this placement from analytics"
                  title="Remove this placement from analytics"
                  className="text-[var(--ink-muted)] hover:text-red-600 disabled:opacity-50"
                >
                  {deletePlacement.isPending &&
                  deletePlacement.variables === p.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <AdShareLinksSection adId={adId} />
      {diag && (
        <div
          data-testid="ad-diagnose-modal"
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={closeDiagnose}
        >
          <div
            className="bg-[var(--surface)] w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-[var(--border)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
              <Stethoscope className="w-4 h-4" />
              <h3 className="font-serif-display text-lg">Diagnose placement</h3>
              <button
                type="button"
                data-testid="ad-diagnose-close"
                onClick={closeDiagnose}
                aria-label="Close"
                className="ml-auto text-[var(--ink-muted)] hover:text-[var(--ink)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 text-xs font-mono-display space-y-3">
              {diagLoading || diag.loading ? (
                <div className="flex items-center gap-2 text-[var(--ink-muted)]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Asking Facebook…
                </div>
              ) : diag.error ? (
                <div className="text-red-600">Diagnostic failed: {diag.error}</div>
              ) : (
                <>
                  <div>
                    <div className="overline mb-1">Likely cause</div>
                    <div
                      data-testid="ad-diagnose-diagnosis"
                      className="text-sm normal-case leading-relaxed text-[var(--ink)] font-serif-display whitespace-pre-line"
                    >
                      {diag.diagnosis}
                    </div>
                  </div>
                  {diag.note && (
                    <div className="text-[var(--accent)] text-[11px] leading-relaxed">
                      {diag.note}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    <div className="text-[var(--ink-muted)]">Stored id</div>
                    <div className="truncate">{diag.stored_fb_post_id || "—"}</div>
                    {diag.healed_post_id && (
                      <>
                        <div className="text-[var(--ink-muted)]">Healed to</div>
                        <div className="truncate">{diag.healed_post_id}</div>
                      </>
                    )}
                    {diag.meta_debug?.detected_post_type && (
                      <>
                        <div className="text-[var(--ink-muted)]">Detected type</div>
                        <div className="truncate">
                          {diag.meta_debug.detected_post_type}
                        </div>
                      </>
                    )}
                    {diag.meta_debug?.insights_metrics_tried?.length > 0 && (
                      <>
                        <div className="text-[var(--ink-muted)]">Metrics tried</div>
                        <div className="truncate">
                          {diag.meta_debug.insights_metrics_tried.join(", ")}
                        </div>
                      </>
                    )}
                    <div className="text-[var(--ink-muted)]">Page</div>
                    <div className="truncate">{diag.page_name || diag.page_id || "—"}</div>
                  </div>
                  {diag.live_metrics && (
                    <div>
                      <div className="overline mb-1">Live metrics (just fetched)</div>
                      <pre className="bg-white border border-[var(--border)] p-2 overflow-x-auto text-[10px]">
                        {JSON.stringify(diag.live_metrics, null, 2)}
                      </pre>
                    </div>
                  )}
                  {diag.meta_debug?.dropped_fields?.length > 0 && (
                    <div>
                      <div className="overline mb-1 text-amber-700">
                        Fields Meta refused
                      </div>
                      <div className="text-amber-700 text-[11px]">
                        {diag.meta_debug.dropped_fields.join(", ")}
                      </div>
                    </div>
                  )}
                  {diag.meta_debug?.raw_response && (
                    <details className="text-[10px]">
                      <summary className="cursor-pointer text-[var(--ink-muted)] hover:text-[var(--ink)]">
                        Raw Facebook response (advanced)
                      </summary>
                      <pre className="bg-white border border-[var(--border)] p-2 overflow-x-auto mt-1">
                        {JSON.stringify(diag.meta_debug.raw_response, null, 2)}
                      </pre>
                    </details>
                  )}
                  {diag.meta_error_body && (
                    <details className="text-[10px]">
                      <summary className="cursor-pointer text-red-600">
                        Meta error body
                      </summary>
                      <pre className="bg-white border border-red-200 p-2 overflow-x-auto mt-1">
                        {JSON.stringify(diag.meta_error_body, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Client share links — lives at the bottom of an ad's analytics panel.
// The plain token arrives ONCE in the create response; the backend
// keeps only its hash. So the fresh URL is held in local state, shown
// with a copy button, and gone on the next reload — mint a new link
// (and revoke the old one) if it's lost.
// ---------------------------------------------------------------------

const EXPIRY_OPTIONS = [
  { value: "", label: "Never expires" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
];

function shareUrlFor(token) {
  return `${window.location.origin}/share/ads/${token}`;
}

function AdShareLinksSection({ adId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("");
  // { id, url } for the link just minted — the only time the URL exists
  // client-side.
  const [fresh, setFresh] = useState(null);

  const { data: links = [], isLoading } = useQuery({
    queryKey: ["ad-share-links", adId],
    queryFn: () => api.listAdShareLinks(adId),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api.createAdShareLink(adId, {
        label: label.trim(),
        expires_in_days: expiry ? Number(expiry) : null,
      }),
    onSuccess: (res) => {
      setFresh({ id: res.link.id, url: shareUrlFor(res.token) });
      setLabel("");
      qc.invalidateQueries({ queryKey: ["ad-share-links", adId] });
      toast.success("Client link created — copy it now, it won't be shown again");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Couldn't create link"),
  });

  const revoke = useMutation({
    mutationFn: (linkId) => api.revokeAdShareLink(adId, linkId),
    onSuccess: (_res, linkId) => {
      if (fresh?.id === linkId) setFresh(null);
      qc.invalidateQueries({ queryKey: ["ad-share-links", adId] });
      toast.success("Link revoked — it now shows 'no longer valid'");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Couldn't revoke link"),
  });

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const active = links.filter((l) => l.status === "active");
  const inactive = links.filter((l) => l.status !== "active");

  return (
    <div
      data-testid={`ad-share-links-${adId}`}
      className="border-t border-[var(--border)] px-4 py-3"
    >
      <button
        type="button"
        data-testid={`ad-share-links-toggle-${adId}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[var(--ink-muted)] hover:text-[var(--ink)] font-mono-display"
      >
        {open ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        <Link2 className="w-3 h-3" />
        Share with client
        {links.length > 0 && (
          <span className="normal-case tracking-normal">
            · {active.length} active
          </span>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-[11px] text-[var(--ink-muted)] leading-snug">
            A client link opens a read-only copy of this analytics view —
            no refresh, no edits — for this one ad. Anyone with the link
            can see it, so send it privately and revoke it when the
            campaign ends. Figures update nightly.
          </p>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input
              data-testid={`ad-share-label-${adId}`}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (e.g. sent to Maria, Sept campaign)"
              maxLength={120}
              className="flex-1 min-w-[200px] border border-[var(--border)] bg-white px-2 py-1.5"
            />
            <select
              data-testid={`ad-share-expiry-${adId}`}
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="border border-[var(--border)] bg-white px-2 py-1.5"
              aria-label="Link expiry"
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid={`ad-share-create-${adId}`}
              onClick={() => create.mutate()}
              disabled={create.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-50"
            >
              {create.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
              Create client link
            </button>
          </div>

          {fresh && (
            <div
              data-testid={`ad-share-fresh-${adId}`}
              className="border border-[var(--accent)] bg-white p-3 text-xs space-y-2"
            >
              <div className="text-[10px] uppercase tracking-wider text-[var(--accent)] font-mono-display">
                New link — copy it now, it will not be shown again
              </div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={fresh.url}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 border border-[var(--border)] bg-[var(--secondary)] px-2 py-1.5 font-mono-display text-[11px]"
                />
                <button
                  type="button"
                  data-testid={`ad-share-copy-${adId}`}
                  onClick={() => copy(fresh.url)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border)] hover:border-[var(--ink)]"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <a
                  href={fresh.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                  aria-label="Open the client view"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading links…
            </div>
          ) : links.length === 0 ? (
            <div className="text-[11px] text-[var(--ink-muted)]">
              No client links yet for this ad.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {[...active, ...inactive].map((l) => (
                <ShareLinkRow
                  key={l.id}
                  link={l}
                  busy={revoke.isPending && revoke.variables === l.id}
                  onRevoke={() => {
                    if (
                      window.confirm(
                        "Revoke this link? Anyone who has it will see " +
                          "'This link is no longer valid'. This cannot be undone.",
                      )
                    ) {
                      revoke.mutate(l.id);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ShareLinkRow({ link, busy, onRevoke }) {
  const isActive = link.status === "active";
  return (
    <li
      data-testid={`ad-share-link-${link.id}`}
      className={
        "flex items-center gap-3 bg-white border border-[var(--border)] px-3 py-2 text-xs" +
        (isActive ? "" : " opacity-60")
      }
    >
      <div className="flex-1 min-w-0">
        <div className="truncate font-medium">
          {link.label || "Untitled link"}
          <span className="ml-2 font-mono-display text-[10px] text-[var(--ink-muted)]">
            {link.token_prefix}…
          </span>
        </div>
        <div className="text-[10px] text-[var(--ink-muted)] font-mono-display truncate">
          Created {new Date(link.created_at).toLocaleDateString()}
          {link.created_by_email ? ` by ${link.created_by_email}` : ""}
          {" · "}
          {link.expires_at
            ? `expires ${new Date(link.expires_at).toLocaleDateString()}`
            : "never expires"}
          {" · "}
          {link.view_count
            ? `viewed ${link.view_count}× (last ${new Date(link.last_viewed_at).toLocaleDateString()})`
            : "not viewed yet"}
          {!isActive && (
            <span className="ml-2 uppercase tracking-wider text-red-600">
              {link.status}
            </span>
          )}
        </div>
      </div>
      {isActive && (
        <button
          type="button"
          data-testid={`ad-share-revoke-${link.id}`}
          onClick={onRevoke}
          disabled={busy}
          aria-label="Revoke this link"
          title="Revoke this link"
          className="inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-red-600 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Ban className="w-3.5 h-3.5" />
          )}
          Revoke
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------
// Nightly refresh status — one line above the ads list. The job runs
// at 03:00 in the property's newsroom timezone and refreshes the last
// 30 days of placements + newsletter engagement. Admins get a "Run
// now" so the job can be verified without waiting for tonight.
// ---------------------------------------------------------------------

function StatsRefreshStatus() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data, refetch } = useQuery({
    queryKey: ["stats-refresh-status"],
    queryFn: () => api.getStatsRefreshStatus(),
    // Poll while a run is in flight so "running" turns into a result.
    refetchInterval: (q) =>
      q.state.data?.last_status === "running" ? 5000 : false,
  });
  const run = useMutation({
    mutationFn: () => api.runStatsRefreshNow(),
    onSuccess: () => {
      toast.success("Refresh started — this can take a few minutes");
      refetch();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Couldn't start refresh"),
  });

  if (!data) return null;
  const s = data.last_summary || {};
  const running = data.last_status === "running";
  let lastRun = "never run yet";
  if (running) {
    lastRun = "running now…";
  } else if (data.last_run_at) {
    lastRun =
      `last run ${new Date(data.last_run_at).toLocaleString()}` +
      (data.last_status === "error"
        ? ` — failed: ${data.last_error}`
        : ` — ${s.placements ?? 0} posts, ${s.articles ?? 0} articles` +
          (data.last_status === "partial" ? " (some rows could not be refreshed)" : ""));
  }

  return (
    <div
      data-testid="stats-refresh-status"
      className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--ink-muted)] font-mono-display"
    >
      <Clock className="w-3 h-3" />
      <span>
        Stats refresh nightly at {data.time_local} (newsroom time), last{" "}
        {data.window_days} days · {lastRun}
        {data.next_run_at && !running
          ? ` · next ${new Date(data.next_run_at).toLocaleString()}`
          : ""}
      </span>
      {isAdmin && (
        <button
          type="button"
          data-testid="stats-refresh-run-now"
          onClick={() => run.mutate()}
          disabled={running || run.isPending}
          className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline disabled:opacity-50"
        >
          {running || run.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
          Run now
        </button>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[var(--border)] p-3"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="font-serif-display text-2xl mt-1 leading-none">
        {value.toLocaleString()}
      </div>
    </div>
  );
}
